import { Delimiters } from 'common-react';
import { TFunction } from 'i18next';
import {
  AVERAGE_RETURN_RATE_DECIMAL_SCALE,
  MAX_AVERAGE_RETURN_RATE_PERCENT,
  MAX_PAYOUT_YEARS,
  MAX_AGE,
  MIN_AVERAGE_RETURN_RATE_PERCENT,
  MIN_PAYOUT_YEARS,
  MIN_AGE,
  RetirementCalculatorField,
  RetirementCalculatorSettingsField,
  SAVINGS_PRODUCTS_ORDER,
  SavingsField,
} from '../../tools/RetirementCalculator.const';
import {
  RetirementCalculatorFormValuesModel,
  RetirementCalculatorSettingsOverridesModel,
  SavingsValuesModel,
} from '../../tools/RetirementCalculator.model';
import { formatAmount } from '../../tools/RetirementCalculator.utils';
import {
  CreateSettingsRowsParamsModel,
  SettingsRowModel,
  SummaryRowModel,
} from './RetirementCalculatorSummary.model';

export const createBasicRows = (
  basicData: RetirementCalculatorFormValuesModel | undefined,
  t: TFunction,
): SummaryRowModel[] => {
  const salary = basicData?.[RetirementCalculatorField.MONTHLY_GROSS_SALARY];
  const employmentType = basicData?.[RetirementCalculatorField.EMPLOYMENT_TYPE];
  const gender = basicData?.[RetirementCalculatorField.GENDER];

  return [
    {
      key: 'salary',
      label: t('summary.basic-data.salary'),
      value: salary === undefined ? Delimiters.DASH : t('summary.amount', { amount: formatAmount(salary) }),
    },
    {
      key: 'birth-year',
      label: t('summary.basic-data.birth-year'),
      value: basicData?.[RetirementCalculatorField.BIRTH_YEAR] ?? Delimiters.DASH,
    },
    {
      key: 'employment-type',
      label: t('summary.basic-data.employment-type'),
      value: employmentType ? t(`summary.employment-type.${employmentType}`) : Delimiters.DASH,
    },
    {
      key: 'gender',
      label: t('summary.basic-data.gender'),
      value: gender ? t(`summary.gender.${gender}`) : Delimiters.DASH,
    },
  ];
};

export const createSavingsRows = (
  savings: SavingsValuesModel | undefined,
  t: TFunction,
): SummaryRowModel[] =>
  SAVINGS_PRODUCTS_ORDER.filter(product => savings?.[product]?.enabled).map(product => {
    const accumulated = savings?.[product]?.[SavingsField.ACCUMULATED] ?? 0;
    const monthly = savings?.[product]?.[SavingsField.MONTHLY_CONTRIBUTION] ?? 0;

    return {
      key: product,
      label: t('summary.savings.product', { product: t(`savings.products.${product}`) }),
      value: monthly
        ? t('summary.amount-with-monthly', {
            amount: formatAmount(accumulated),
            monthly: formatAmount(monthly),
          })
        : t('summary.amount', { amount: formatAmount(accumulated) }),
    };
  });

export const createSettingsRows = ({
  settings,
  settingsOverrides,
  gender,
  onSettingsOverridesChange,
  t,
}: CreateSettingsRowsParamsModel): SettingsRowModel[] => {
  const getSettingsValue = (field: RetirementCalculatorSettingsField, fallback?: number) =>
    settingsOverrides?.[field] ?? fallback;

  const changeSetting = (field: RetirementCalculatorSettingsField, value?: number) => {
    const nextSettingsOverrides: RetirementCalculatorSettingsOverridesModel = {
      ...settingsOverrides,
    };

    if (value === undefined) {
      delete nextSettingsOverrides[field];
    } else {
      nextSettingsOverrides[field] = value;
    }

    onSettingsOverridesChange?.(nextSettingsOverrides);
  };

  const returnRate = getSettingsValue(
    RetirementCalculatorSettingsField.AVERAGE_RETURN_RATE,
    settings.averageReturnRate,
  );

  return [
    {
      field: RetirementCalculatorSettingsField.AVERAGE_RETURN_RATE,
      label: t('summary.advanced.return-rate'),
      unit: t('summary.units.percent'),
      value: returnRate === undefined ? undefined : Math.round(returnRate * 100 * 100) / 100,
      min: MIN_AVERAGE_RETURN_RATE_PERCENT,
      max: MAX_AVERAGE_RETURN_RATE_PERCENT,
      decimalScale: AVERAGE_RETURN_RATE_DECIMAL_SCALE,
      onChange: percent =>
        changeSetting(
          RetirementCalculatorSettingsField.AVERAGE_RETURN_RATE,
          percent === undefined ? undefined : percent / 100,
        ),
    },
    {
      field: RetirementCalculatorSettingsField.RETIREMENT_AGE,
      label: t('summary.advanced.retirement-age'),
      unit: t('summary.units.years'),
      value: getSettingsValue(
        RetirementCalculatorSettingsField.RETIREMENT_AGE,
        gender ? settings.retirementAge[gender] : undefined,
      ),
      min: MIN_AGE,
      max: MAX_AGE,
      decimalScale: 0,
      disabled: !gender,
      onChange: value => changeSetting(RetirementCalculatorSettingsField.RETIREMENT_AGE, value),
    },
    {
      field: RetirementCalculatorSettingsField.PAYOUT_YEARS,
      label: t('summary.advanced.payout-years'),
      unit: t('summary.units.years'),
      value: getSettingsValue(
        RetirementCalculatorSettingsField.PAYOUT_YEARS,
        settings.payoutYears,
      ),
      min: MIN_PAYOUT_YEARS,
      max: MAX_PAYOUT_YEARS,
      decimalScale: 0,
      onChange: value => changeSetting(RetirementCalculatorSettingsField.PAYOUT_YEARS, value),
    },
  ];
};
